import { useEffect, useRef } from 'react';
import { Download, MoreVertical, Share, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';

/**
 * Barra fina fixa no topo (somente mobile) com a ação de instalar o PWA.
 *
 * Posiciona-se logo abaixo da barra de ambiente (`--env-banner-h`) e expõe a
 * própria altura via `--install-bar-h`, que o `SidebarLayout` (header + conteúdo)
 * consome para se deslocar — assim a barra não cobre o cabeçalho do app.
 *
 * Convive com a barra de offline: esta tem z-index maior e ocupa o mesmo slot
 * (abaixo do ambiente), então, no caso raro de offline + instalável, a de
 * offline fica por cima sem ser prejudicada.
 *
 * `sm:hidden` zera a altura no desktop (display:none → offsetHeight 0). No
 * desktop não há banner próprio: quem quiser instalar usa o botão nativo do
 * navegador (ícone de instalação na barra de endereço do Chrome/Edge).
 */
export function InstallBar() {
  const { isInstallable, installMode, promptInstall, dismissPrompt } = useInstallPrompt();
  const ref = useRef<HTMLDivElement>(null);

  // Mede a altura real da barra e escreve em `--install-bar-h`. Em desktop
  // (`sm:hidden`) ou quando não instalável, a referência é nula/0 → reseta.
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) {
      root.style.setProperty('--install-bar-h', '0px');
      return;
    }
    const update = () => root.style.setProperty('--install-bar-h', `${el.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty('--install-bar-h', '0px');
    };
  }, [isInstallable, installMode]);

  if (!isInstallable) return null;

  const renderInstructions = () => {
    switch (installMode) {
      case 'manual-ios':
        return (
          <span className="flex items-center gap-1 flex-wrap">
            Toque em <Share className="inline h-3 w-3" /> e &quot;Adicionar à Tela de Início&quot;
          </span>
        );
      case 'manual-android-firefox':
        return 'Menu do Firefox › "Adicionar à tela inicial"';
      case 'manual-android-generic':
        return (
          <span className="flex items-center gap-1 flex-wrap">
            Menu <MoreVertical className="inline h-3 w-3" /> do navegador › &quot;Instalar aplicativo&quot;
          </span>
        );
      default:
        return 'Acesso rápido e uso offline';
    }
  };

  return (
    <div
      ref={ref}
      className="fixed inset-x-0 top-[var(--env-banner-h,0px)] z-40 flex items-center gap-2 border-b border-border bg-card px-4 py-2 shadow-sm animate-in slide-in-from-top fade-in duration-300 sm:hidden"
    >
      <Download className="h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold leading-tight text-card-foreground">Instalar JurisApp</p>
        <p className="text-[11px] leading-tight text-muted-foreground">{renderInstructions()}</p>
      </div>
      {installMode === 'native' && (
        <Button size="sm" className="h-7 shrink-0 px-2.5 text-xs" onClick={() => void promptInstall()}>
          Instalar
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={dismissPrompt}
        aria-label="Dispensar"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default InstallBar;
